import type { ComponentProps, JSX, ReactElement } from 'react';

import { Icon } from '@change-corgi/design-system/components/icon';
import { getSocialColor, getSocialIcon } from '@change-corgi/design-system/components/social';
import { Flex } from '@change-corgi/design-system/layout';
import { buttonResetStyles } from '@change-corgi/design-system/reset';
import { Text } from '@change-corgi/design-system/typography';

import { Join } from 'src/app/shared/components/layout/misc';

import type { PartialComment, PartialPetition, ShareConfig } from './hooks/useCommentShares';
import { useCommentShares } from './hooks/useCommentShares';

type Props = ShareConfig & {
	comment: PartialComment;
	petition: PartialPetition;
	viewContext: string;
	size?: ComponentProps<typeof Text>['size'];
	iconSize?: ComponentProps<typeof Icon>['size'];
	separator?: ReactElement | string;
};

export function CommentShares({
	comment,
	petition,
	viewContext,
	iconSize,
	size,
	separator,
	...rest
}: Props & ComponentProps<typeof Flex>): JSX.Element {
	const {
		data: { shareActions, labels, a11Labels },
	} = useCommentShares(comment, petition, { viewContext });

	return (
		<Join separator={separator}>
			{shareActions.map(({ channel, action }) => (
				<Flex key={channel} sx={{ alignItems: 'center' }} data-qa={`comment-share-${channel}`} {...rest}>
					<button type="button" sx={buttonResetStyles} onClick={action} aria-label={a11Labels[channel]}>
						<Text
							size={size}
							sx={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
							color={getSocialColor(channel)}
						>
							<Icon icon={getSocialIcon(channel)} color={getSocialColor(channel)} size={iconSize} />
							{labels[channel]}
						</Text>
					</button>
				</Flex>
			))}
		</Join>
	);
}
